/**
 * Biblioteca de Farmacovigilancia (21/10/2026, pedido de Fernando Ramírez).
 *
 * Carpetas fijas (certificación MSPAS, plan, SOP, acuerdos, notificaciones y
 * reportes adversos, conciliaciones, capacitaciones, auditorías). Nada se
 * borra: un documento se reemplaza por una versión nueva y la anterior queda
 * en el historial, con la bitácora de quién cargó, corrigió o descargó.
 *
 * El formulario de carga guarda un borrador mientras se escribe (se recupera
 * al recargar, con "Descartar") y cada carga lleva un client_uuid para que un
 * reintento no la duplique. El archivo en sí no se puede guardar en el
 * borrador: si se recupera, se pide elegirlo de nuevo.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Button, Card, CardActionArea, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Grid, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Tooltip, Typography, Alert, Snackbar, Skeleton, Stack, Divider,
} from '@mui/material';
import {
  Add, CloudUpload, GetApp, Visibility, History, Edit, Search, FolderOpen, VerifiedUser,
  Description, Handshake, Campaign, FactCheck, School, Policy, Rule, Restore,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { usePermissions } from '../hooks/usePermissions';

const ICONOS = {
  certificacion: <VerifiedUser />, plan: <Description />, sop: <Rule />, acuerdos: <Handshake />,
  notificaciones: <Campaign />, conciliaciones: <FactCheck />, capacitaciones: <School />, auditorias: <Policy />,
};

// Qué datos pide cada carpeta además del título y el archivo
const CAMPOS = {
  certificacion: ['fecha_documento', 'fecha_vencimiento'],
  plan: ['version', 'fecha_documento', 'fecha_vencimiento'],
  sop: ['codigo', 'version', 'fecha_documento', 'fecha_vencimiento'],
  acuerdos: ['laboratorio', 'version', 'fecha_documento', 'fecha_vencimiento'],
  notificaciones: ['laboratorio', 'fecha_documento'],
  conciliaciones: ['laboratorio', 'periodo', 'fecha_documento'],
  capacitaciones: ['fecha_documento'],
  auditorias: ['laboratorio', 'fecha_documento'],
};
const ETIQUETA_LAB = {
  acuerdos: 'Laboratorio', conciliaciones: 'Laboratorio', notificaciones: 'Laboratorio o destinatario',
  auditorias: 'Auditor o entidad',
};
const ACCION = {
  carga: 'Cargó el documento', nueva_version: 'Cargó una versión nueva', edicion: 'Corrigió datos', descarga: 'Descargó',
};

const VACIO = {
  titulo: '', laboratorio: '', codigo: '', version: '', periodo: '',
  fecha_documento: '', fecha_vencimiento: '', observaciones: '',
};

const StyledTableContainer = styled(TableContainer)({
  borderRadius: '16px',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
});
const StyledTableHead = styled(TableHead)({
  backgroundColor: '#f8fafc',
  '& th': { fontWeight: 600, color: '#475569', fontSize: '0.875rem' },
});

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
// 'YYYY-MM-DD' armada sin zona horaria: con new Date() sale un día antes en Guatemala
function fmtFecha(iso) {
  if (!iso) return '—';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  return `${d} ${MESES[Number(m) - 1]} ${y}`;
}
function fmtMomento(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-GT', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}
function fmtTamano(b) {
  if (b == null) return '';
  return b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`;
}
function diasPara(iso) {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map(Number);
  const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
  return Math.round((new Date(y, m - 1, d) - hoy) / 86400000);
}
function ChipVence({ fecha, etiqueta = 'Vence' }) {
  const dias = diasPara(fecha);
  if (dias === null) return <Typography variant="body2" color="text.secondary">—</Typography>;
  const estado = dias < 0 ? { c: 'error', t: `Vencido hace ${-dias} d` }
    : dias <= 90 ? { c: 'warning', t: `En ${dias} d` } : { c: 'default', t: null };
  return (
    <Box>
      <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>{etiqueta} {fmtFecha(fecha)}</Typography>
      {estado.t && <Chip size="small" color={estado.c} label={estado.t} sx={{ mt: 0.5, borderRadius: '6px' }} />}
    </Box>
  );
}

const token = () => localStorage.getItem('token');
const usuarioId = () => {
  try { return JSON.parse(localStorage.getItem('currentUser') || '{}').id || 0; } catch { return 0; }
};
const nuevoUuid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID()
  : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

async function api(url, opts = {}) {
  const r = await fetch(`/api/farmacovigilancia${url}`, {
    ...opts, headers: { Authorization: `Bearer ${token()}`, ...(opts.headers || {}) },
  });
  const datos = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(datos.error || 'No se pudo completar la operación.');
  return datos;
}

async function abrirArchivo(doc, ver) {
  const r = await fetch(`/api/farmacovigilancia/documentos/${doc.id}/archivo${ver ? '?ver=1' : ''}`, {
    headers: { Authorization: `Bearer ${token()}` },
  });
  if (!r.ok) throw new Error('No se pudo abrir el archivo.');
  const url = URL.createObjectURL(await r.blob());
  if (ver) {
    window.open(url, '_blank', 'noopener');
  } else {
    const a = document.createElement('a');
    a.href = url; a.download = doc.nombre_archivo;
    document.body.appendChild(a); a.click(); a.remove();
  }
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

// ------------------------------------------------------------------
// Borrador del formulario de carga (regla de persistencia)
// ------------------------------------------------------------------
const VIGENCIA = 7 * 86400000;
function useBorrador(nombre, valor, restaurar, activo) {
  const clave = `regulatorio:borrador:${usuarioId()}:${nombre}`;
  const [recuperado, setRecuperado] = useState(false);
  const tocado = useRef(false);
  const apagado = useRef(false);
  const revisado = useRef(false);

  useEffect(() => {
    if (!activo || revisado.current) return;
    revisado.current = true;
    try {
      const g = JSON.parse(localStorage.getItem(clave) || 'null');
      if (g && Date.now() - g.momento < VIGENCIA) { restaurar(g.valor); setRecuperado(true); tocado.current = true; }
      else if (g) localStorage.removeItem(clave);
    } catch { /* sin almacenamiento: el formulario sirve igual */ }
  }, [activo]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!activo || !revisado.current || apagado.current || !tocado.current) return undefined;
    const t = setTimeout(() => {
      try { localStorage.setItem(clave, JSON.stringify({ momento: Date.now(), valor })); } catch { /* lleno */ }
    }, 300);
    return () => clearTimeout(t);
  }, [valor, activo, clave]);

  const limpiar = useCallback(() => {
    apagado.current = true;
    try { localStorage.removeItem(clave); } catch { /* nada */ }
    setRecuperado(false);
  }, [clave]);
  const tocar = useCallback(() => { tocado.current = true; }, []);
  return { recuperado, limpiar, escuchar: { onInputCapture: tocar, onClickCapture: tocar } };
}

// ------------------------------------------------------------------
// Diálogo de carga (documento nuevo, versión nueva o corrección de datos)
// ------------------------------------------------------------------
function DialogoCarga({ modo, carpeta, doc, onCerrar, onGuardado }) {
  // modo: 'nuevo' | 'version' | 'editar'
  const inicial = useMemo(() => (doc ? {
    ...VACIO, ...Object.fromEntries(Object.keys(VACIO).map((k) => [k, doc[k] || ''])),
  } : VACIO), [doc]);
  const [f, setF] = useState(inicial);
  const [uuid, setUuid] = useState(nuevoUuid);
  const [archivo, setArchivo] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState('');

  const nombreBorrador = `fv:${modo}:${doc?.id || carpeta.id}`;
  const valor = useMemo(() => ({ f, uuid }), [f, uuid]);
  const restaurar = useCallback((g) => { setF({ ...VACIO, ...g.f }); if (g.uuid) setUuid(g.uuid); }, []);
  const b = useBorrador(nombreBorrador, valor, restaurar, true);

  const campos = CAMPOS[carpeta.id] || [];
  const cambia = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const exigeVence = carpeta.exigeVence;

  const cancelar = () => { b.limpiar(); onCerrar(); };
  const descartar = () => { b.limpiar(); setF(inicial); setUuid(nuevoUuid()); };

  const guardar = async () => {
    setError('');
    if (!f.titulo.trim()) { setError('Falta el título del documento.'); return; }
    if (modo !== 'editar' && !archivo) { setError('Falta elegir el archivo.'); return; }
    if (exigeVence && !f.fecha_vencimiento) { setError('La certificación necesita su fecha de vencimiento.'); return; }
    setEnviando(true);
    try {
      let res;
      if (modo === 'editar') {
        res = await api(`/documentos/${doc.id}`, {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(Object.fromEntries(['titulo', ...campos, 'observaciones'].map((k) => [k, f[k]]))),
        });
      } else {
        const fd = new FormData();
        Object.entries(f).forEach(([k, v]) => { if (v) fd.append(k, v); });
        fd.append('client_uuid', uuid);
        if (modo === 'version') fd.append('reemplaza_id', doc.id); else fd.append('carpeta', carpeta.id);
        fd.append('archivo', archivo);
        res = await api('/documentos', { method: 'POST', body: fd });
      }
      b.limpiar();
      onGuardado(res.documento, modo);
    } catch (e) {
      setError(e.message);
    } finally {
      setEnviando(false);
    }
  };

  const titulo = modo === 'nuevo' ? `Cargar en ${carpeta.nombre}`
    : modo === 'version' ? 'Cargar versión nueva' : 'Corregir datos';

  return (
    <Dialog open onClose={cancelar} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '14px' } }}>
      <DialogTitle sx={{ fontWeight: 700 }}>{titulo}</DialogTitle>
      <DialogContent dividers {...b.escuchar}>
        <Stack spacing={2}>
          {b.recuperado && (
            <Alert severity="info" icon={<Restore />} action={<Button color="inherit" size="small" onClick={descartar}>Descartar</Button>}>
              Se recuperó lo que estaba escribiendo.{modo !== 'editar' && ' Vuelva a elegir el archivo.'}
            </Alert>
          )}
          {modo === 'version' && (
            <Alert severity="warning" variant="outlined">
              La versión actual ({doc.nombre_archivo}) queda en el historial y deja de ser la vigente.
            </Alert>
          )}
          <TextField label="Título" required value={f.titulo} onChange={cambia('titulo')} fullWidth autoFocus />
          {/* Box: el Grid con márgenes negativos se corre dentro de un Stack */}
          <Box>
          <Grid container spacing={2}>
            {campos.includes('laboratorio') && (
              <Grid item xs={12} sm={6}>
                <TextField label={ETIQUETA_LAB[carpeta.id] || 'Laboratorio'} value={f.laboratorio} onChange={cambia('laboratorio')} fullWidth placeholder="Fresenius Kabi, Sanderson, Grifols…" />
              </Grid>
            )}
            {campos.includes('codigo') && (
              <Grid item xs={12} sm={6}><TextField label="Código" value={f.codigo} onChange={cambia('codigo')} fullWidth placeholder="POE-PV-001" /></Grid>
            )}
            {campos.includes('version') && (
              <Grid item xs={12} sm={6}><TextField label="Versión" value={f.version} onChange={cambia('version')} fullWidth placeholder="1.0" /></Grid>
            )}
            {campos.includes('periodo') && (
              <Grid item xs={12} sm={6}><TextField label="Periodo" value={f.periodo} onChange={cambia('periodo')} fullWidth placeholder="Jul–Sep 2026" /></Grid>
            )}
            {campos.includes('fecha_documento') && (
              <Grid item xs={12} sm={6}>
                <TextField label="Fecha del documento" type="date" value={f.fecha_documento} onChange={cambia('fecha_documento')} fullWidth InputLabelProps={{ shrink: true }} />
              </Grid>
            )}
            {campos.includes('fecha_vencimiento') && (
              <Grid item xs={12} sm={6}>
                <TextField label={carpeta.vence || 'Vence'} type="date" required={exigeVence} value={f.fecha_vencimiento} onChange={cambia('fecha_vencimiento')} fullWidth InputLabelProps={{ shrink: true }}
                  helperText={exigeVence ? 'Se avisa antes de que venza' : 'Opcional'} />
              </Grid>
            )}
          </Grid>
          </Box>
          <TextField label="Observaciones" value={f.observaciones} onChange={cambia('observaciones')} fullWidth multiline minRows={2} />
          {modo !== 'editar' && (
            <Box>
              <Button component="label" variant="outlined" startIcon={<CloudUpload />} sx={{ borderRadius: '6px' }}>
                {archivo ? 'Cambiar archivo' : 'Elegir archivo'}
                <input hidden type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.msg,.eml"
                  onChange={(e) => setArchivo(e.target.files?.[0] || null)} />
              </Button>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {archivo ? `${archivo.name} · ${fmtTamano(archivo.size)}` : 'PDF, Word, Excel, PowerPoint, imagen o correo (.msg, .eml), hasta 50 MB.'}
              </Typography>
            </Box>
          )}
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={cancelar} disabled={enviando}>Cancelar</Button>
        <Button variant="contained" onClick={guardar} disabled={enviando} sx={{ borderRadius: '6px' }}>
          {enviando ? 'Guardando…' : modo === 'editar' ? 'Guardar cambios' : 'Cargar documento'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ------------------------------------------------------------------
// Historial: versiones y bitácora
// ------------------------------------------------------------------
function DialogoHistorial({ doc, onCerrar, onError }) {
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    api(`/documentos/${doc.id}/historial`).then(setDatos).catch((e) => setError(e.message));
  }, [doc.id]);
  const abrir = (v) => abrirArchivo(v, false).catch((e) => onError(e.message));

  return (
    <Dialog open onClose={onCerrar} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '14px' } }}>
      <DialogTitle sx={{ fontWeight: 700 }}>Historial · {doc.titulo}</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error">{error}</Alert>}
        {!datos && !error && <Skeleton variant="rounded" height={160} />}
        {datos && (
          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Versiones</Typography>
              <Table size="small">
                <TableHead><TableRow><TableCell>Archivo</TableCell><TableCell>Versión</TableCell><TableCell>Cargado</TableCell><TableCell>Estado</TableCell><TableCell /></TableRow></TableHead>
                <TableBody>
                  {datos.versiones.map((v) => (
                    <TableRow key={v.id} hover>
                      <TableCell>{v.nombre_archivo}<Typography variant="caption" display="block" color="text.secondary">{fmtTamano(v.tamano)}</Typography></TableCell>
                      <TableCell>{v.version || '—'}</TableCell>
                      <TableCell>{fmtMomento(v.created_at)}<Typography variant="caption" display="block" color="text.secondary">{v.created_by_name || ''}</Typography></TableCell>
                      <TableCell>{v.vigente ? <Chip size="small" color="success" label="Vigente" sx={{ borderRadius: '6px' }} /> : <Chip size="small" label={`Reemplazada ${fmtFecha(v.reemplazado_at)}`} sx={{ borderRadius: '6px' }} />}</TableCell>
                      <TableCell align="right"><Tooltip title="Descargar"><IconButton size="small" onClick={() => abrir(v)} aria-label={`Descargar ${v.nombre_archivo}`}><GetApp fontSize="small" /></IconButton></Tooltip></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
            <Divider />
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Bitácora</Typography>
              <Table size="small">
                <TableHead><TableRow><TableCell>Cuándo</TableCell><TableCell>Quién</TableCell><TableCell>Qué hizo</TableCell></TableRow></TableHead>
                <TableBody>
                  {datos.bitacora.map((x, i) => (
                    <TableRow key={i}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtMomento(x.created_at)}</TableCell>
                      <TableCell>{x.username || '—'}</TableCell>
                      <TableCell>
                        {ACCION[x.accion] || x.accion}
                        {x.accion === 'edicion' && x.detalle?.cambios && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            {Object.entries(x.detalle.cambios).map(([k, c]) => `${k}: ${c.antes || '—'} → ${c.despues || '—'}`).join(' · ')}
                          </Typography>
                        )}
                        {x.detalle?.archivo && x.accion !== 'edicion' && (
                          <Typography variant="caption" display="block" color="text.secondary">{x.detalle.archivo}</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onCerrar}>Cerrar</Button></DialogActions>
    </Dialog>
  );
}

// ------------------------------------------------------------------
// Pantalla
// ------------------------------------------------------------------
export default function Farmacovigilancia() {
  const { canWrite } = usePermissions();
  const [carpetas, setCarpetas] = useState(null);
  const [puedeEscribir, setPuedeEscribir] = useState(false);
  const [errorCarpetas, setErrorCarpetas] = useState('');
  const [sel, setSel] = useState(() => {
    try { return localStorage.getItem('regulatorio:fv:carpeta') || 'certificacion'; } catch { return 'certificacion'; }
  });
  const [docs, setDocs] = useState(null);
  const [errorDocs, setErrorDocs] = useState('');
  const [buscar, setBuscar] = useState('');
  const [dialogo, setDialogo] = useState(null); // { modo, doc }
  const [historial, setHistorial] = useState(null);
  const [aviso, setAviso] = useState(null);

  const carpeta = carpetas?.find((c) => c.id === sel);

  const cargarCarpetas = useCallback(() => {
    setErrorCarpetas('');
    api('/carpetas')
      .then((d) => { setCarpetas(d.carpetas); setPuedeEscribir(d.puede_escribir); })
      .catch((e) => setErrorCarpetas(e.message));
  }, []);
  const cargarDocs = useCallback(() => {
    setDocs(null); setErrorDocs('');
    api(`/documentos?carpeta=${sel}`).then(setDocs).catch((e) => setErrorDocs(e.message));
  }, [sel]);

  useEffect(() => { cargarCarpetas(); }, [cargarCarpetas]);
  useEffect(() => { cargarDocs(); }, [cargarDocs]);
  useEffect(() => { try { localStorage.setItem('regulatorio:fv:carpeta', sel); } catch { /* nada */ } }, [sel]);

  const filtrados = useMemo(() => {
    const q = buscar.trim().toLowerCase();
    if (!q || !docs) return docs;
    return docs.filter((d) => [d.titulo, d.laboratorio, d.codigo, d.nombre_archivo, d.periodo, d.observaciones]
      .some((v) => (v || '').toLowerCase().includes(q)));
  }, [docs, buscar]);

  // Lo que vence en 90 días o ya venció, de todas las carpetas
  const porVencer = (carpetas || []).filter((c) => c.proximo_vencimiento && diasPara(c.proximo_vencimiento) <= 90);

  const guardado = (_doc, modo) => {
    setDialogo(null);
    setAviso(modo === 'editar' ? 'Datos corregidos.' : modo === 'version' ? 'Versión nueva cargada.' : 'Documento cargado.');
    cargarDocs(); cargarCarpetas();
  };
  const abrir = (doc, ver) => abrirArchivo(doc, ver).catch((e) => setAviso(e.message));
  const escribe = puedeEscribir && canWrite();
  const campos = CAMPOS[sel] || [];

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b' }}>Farmacovigilancia</Typography>
        <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5, maxWidth: 720 }}>
          Certificación, plan, procedimientos, acuerdos con los laboratorios y la evidencia que piden las auditorías.
          Nada se borra: al reemplazar un documento, la versión anterior queda en su historial.
        </Typography>
      </Box>

      {errorCarpetas && (
        <Alert severity="error" sx={{ mb: 3 }} action={<Button color="inherit" size="small" onClick={cargarCarpetas}>Reintentar</Button>}>
          {errorCarpetas}
        </Alert>
      )}

      {porVencer.length > 0 && (
        <Alert severity={porVencer.some((c) => diasPara(c.proximo_vencimiento) < 0) ? 'error' : 'warning'} sx={{ mb: 3 }}>
          {porVencer.map((c) => {
            const d = diasPara(c.proximo_vencimiento);
            return `${c.nombre}: ${d < 0 ? `venció el ${fmtFecha(c.proximo_vencimiento)}` : `${c.vence?.toLowerCase() || 'vence'} el ${fmtFecha(c.proximo_vencimiento)} (en ${d} días)`}`;
          }).join(' · ')}
        </Alert>
      )}

      {/* Carpetas */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {(carpetas || Array.from({ length: 8 }, (_, i) => ({ id: `s${i}` }))).map((c) => (
          <Grid item xs={6} sm={4} md={3} key={c.id}>
            {!carpetas ? <Skeleton variant="rounded" height={92} sx={{ borderRadius: '10px' }} /> : (
              <Card variant="outlined" sx={{
                borderRadius: '10px', boxShadow: 'none', height: '100%',
                borderColor: sel === c.id ? '#3b82f6' : '#e2e8f0',
                backgroundColor: sel === c.id ? '#eff6ff' : '#fff',
              }}>
                <CardActionArea onClick={() => setSel(c.id)} sx={{ p: 2, height: '100%' }} aria-pressed={sel === c.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: sel === c.id ? '#1d4ed8' : '#475569' }}>
                    {ICONOS[c.id] || <FolderOpen />}
                    <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>{c.nombre}</Typography>
                  </Box>
                  <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 1 }}>
                    {c.total === 0 ? 'Sin documentos' : `${c.total} documento${c.total === 1 ? '' : 's'}`}
                    {c.proximo_vencimiento ? ` · ${c.vence?.toLowerCase() || 'vence'} ${fmtFecha(c.proximo_vencimiento)}` : ''}
                  </Typography>
                </CardActionArea>
              </Card>
            )}
          </Grid>
        ))}
      </Grid>

      {carpeta && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>{carpeta.nombre}</Typography>
              <Typography variant="body2" sx={{ color: '#64748b' }}>{carpeta.descripcion}</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
              <TextField size="small" placeholder="Buscar en esta carpeta" value={buscar} onChange={(e) => setBuscar(e.target.value)}
                InputProps={{ startAdornment: <Search sx={{ color: '#94a3b8', mr: 1 }} /> }} />
              {escribe && (
                <Button variant="contained" startIcon={<Add />} onClick={() => setDialogo({ modo: 'nuevo', doc: null })} sx={{ borderRadius: '6px' }}>
                  Cargar documento
                </Button>
              )}
            </Box>
          </Box>

          <StyledTableContainer component={Paper}>
            <Table>
              <StyledTableHead>
                <TableRow>
                  <TableCell>Documento</TableCell>
                  {(campos.includes('laboratorio') || campos.includes('codigo') || campos.includes('periodo')) && <TableCell>Datos</TableCell>}
                  <TableCell>Fecha</TableCell>
                  {campos.includes('fecha_vencimiento') && <TableCell>{carpeta.vence}</TableCell>}
                  <TableCell>Cargado</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </StyledTableHead>
              <TableBody>
                {errorDocs && (
                  <TableRow><TableCell colSpan={6}>
                    <Alert severity="error" action={<Button color="inherit" size="small" onClick={cargarDocs}>Reintentar</Button>}>{errorDocs}</Alert>
                  </TableCell></TableRow>
                )}
                {!docs && !errorDocs && [0, 1, 2].map((i) => (
                  <TableRow key={i}><TableCell colSpan={6}><Skeleton height={36} /></TableCell></TableRow>
                ))}
                {filtrados && filtrados.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                      <FolderOpen sx={{ color: '#cbd5e1', fontSize: 36 }} />
                      <Typography variant="body1" sx={{ color: '#475569', mt: 1 }}>
                        {buscar ? 'Ningún documento coincide con la búsqueda.' : `Todavía no hay documentos en ${carpeta.nombre}.`}
                      </Typography>
                      {!buscar && escribe && (
                        <Button sx={{ mt: 1.5 }} startIcon={<Add />} onClick={() => setDialogo({ modo: 'nuevo', doc: null })}>Cargar el primero</Button>
                      )}
                    </TableCell>
                  </TableRow>
                )}
                {filtrados && filtrados.map((d) => (
                  <TableRow key={d.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>{d.titulo}</Typography>
                      <Typography variant="caption" sx={{ color: '#64748b' }}>
                        {d.nombre_archivo} · {fmtTamano(d.tamano)}{d.versiones > 1 ? ` · ${d.versiones} versiones` : ''}
                      </Typography>
                      {d.observaciones && <Typography variant="caption" display="block" sx={{ color: '#94a3b8' }}>{d.observaciones}</Typography>}
                    </TableCell>
                    {(campos.includes('laboratorio') || campos.includes('codigo') || campos.includes('periodo')) && (
                      <TableCell>
                        <Typography variant="body2">{[d.laboratorio, d.codigo, d.periodo].filter(Boolean).join(' · ') || '—'}</Typography>
                        {d.version && <Typography variant="caption" sx={{ color: '#64748b' }}>Versión {d.version}</Typography>}
                      </TableCell>
                    )}
                    <TableCell sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{fmtFecha(d.fecha_documento)}</TableCell>
                    {campos.includes('fecha_vencimiento') && <TableCell><ChipVence fecha={d.fecha_vencimiento} etiqueta="" /></TableCell>}
                    <TableCell>
                      <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>{fmtMomento(d.created_at)}</Typography>
                      <Typography variant="caption" sx={{ color: '#64748b' }}>{d.created_by_name || ''}</Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {/pdf|image/.test(d.mime || '') && (
                        <Tooltip title="Ver"><IconButton size="small" onClick={() => abrir(d, true)} aria-label={`Ver ${d.titulo}`}><Visibility fontSize="small" /></IconButton></Tooltip>
                      )}
                      <Tooltip title="Descargar"><IconButton size="small" onClick={() => abrir(d, false)} aria-label={`Descargar ${d.titulo}`}><GetApp fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Historial"><IconButton size="small" onClick={() => setHistorial(d)} aria-label={`Historial de ${d.titulo}`}><History fontSize="small" /></IconButton></Tooltip>
                      {escribe && (
                        <>
                          <Tooltip title="Cargar versión nueva"><IconButton size="small" onClick={() => setDialogo({ modo: 'version', doc: d })} aria-label={`Cargar versión nueva de ${d.titulo}`}><CloudUpload fontSize="small" /></IconButton></Tooltip>
                          <Tooltip title="Corregir datos"><IconButton size="small" onClick={() => setDialogo({ modo: 'editar', doc: d })} aria-label={`Corregir datos de ${d.titulo}`}><Edit fontSize="small" /></IconButton></Tooltip>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </StyledTableContainer>
          {!escribe && carpetas && (
            <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 1.5 }}>
              Su usuario solo consulta. Para cargar documentos se necesita permiso de escritura.
            </Typography>
          )}
        </>
      )}

      {dialogo && carpeta && (
        <DialogoCarga modo={dialogo.modo} carpeta={carpeta} doc={dialogo.doc} onCerrar={() => setDialogo(null)} onGuardado={guardado} />
      )}
      {historial && <DialogoHistorial doc={historial} onCerrar={() => setHistorial(null)} onError={setAviso} />}
      <Snackbar open={!!aviso} autoHideDuration={4000} onClose={() => setAviso(null)} message={aviso} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  );
}
